import DashboardLayout from "@/layout/dashboard-layout";
import { ReactNode, useMemo, useState } from "react";
import { Head, router } from "@inertiajs/react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Newspaper,
  Search,
  Eye,
  ExternalLink,
  Sparkles,
  CheckCircle2,
  Clock,
  AlertTriangle,
} from "lucide-react";

type Article = {
  id: number;
  title: string;
  source: string | null;
  sector: string | null;
  status: string;
  status_label: string;
  published_at: string | null;
  created_at: string;
  source_url: string;
};

type Stats = {
  total: number;
  pending: number;
  rewritten: number;
  converted: number;
  failed: number;
};

type Props = {
  articles: Article[];
  filters: { status: { value: string; label: string }[] };
  stats: Stats;
};

const statusBadge = (status: string, label: string) => {
  switch (status) {
    case "rewritten":
      return <Badge variant="default">{label}</Badge>;
    case "converted":
      return <Badge className="bg-green-600 hover:bg-green-600/90">{label}</Badge>;
    case "pending":
      return <Badge variant="secondary">{label}</Badge>;
    case "failed":
      return <Badge variant="destructive">{label}</Badge>;
    default:
      return <Badge variant="outline">{label}</Badge>;
  }
};

const ScrapedArticlesIndex = ({ articles, filters, stats }: Props) => {
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  const filtered = useMemo(
    () =>
      articles.filter((a) => {
        const matchesSearch =
          a.title?.toLowerCase().includes(search.toLowerCase()) ||
          (a.source ?? "").toLowerCase().includes(search.toLowerCase());
        const matchesStatus = statusFilter === "all" || a.status === statusFilter;
        return matchesSearch && matchesStatus;
      }),
    [articles, search, statusFilter]
  );

  const cards = [
    { label: "Total capturadas", value: stats.total, icon: Newspaper, hint: "Todas as fontes" },
    { label: "Reescritas", value: stats.rewritten, icon: Sparkles, hint: "Aguardando revisão" },
    { label: "Publicadas", value: stats.converted, icon: CheckCircle2, hint: "Convertidas em post" },
    { label: "Pendentes / Falhas", value: stats.pending + stats.failed, icon: Clock, hint: "Sem reescrita válida" },
  ];

  return (
    <div className="space-y-6">
      <Head title="Curadoria de Notícias" />

      <div>
        <h1 className="text-3xl font-bold">Curadoria de Notícias</h1>
        <p className="text-muted-foreground">
          Revise as matérias capturadas e reescritas pela IA antes de publicar.
        </p>
      </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        {cards.map((card) => (
          <Card key={card.label}>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">{card.label}</CardTitle>
              <card.icon className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{card.value}</div>
              <p className="text-xs text-muted-foreground">{card.hint}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Matérias capturadas</CardTitle>
          <CardDescription>
            Conteúdo varrido das fontes configuradas e reescrito seguindo princípios editoriais.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="mb-4 flex items-center space-x-4">
            <div className="relative flex-1">
              <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Buscar por título ou fonte..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-8"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[200px]">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos os status</SelectItem>
                {filters.status.map((s) => (
                  <SelectItem key={s.value} value={s.value}>
                    {s.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {filtered.length === 0 ? (
            <div className="flex flex-col items-center justify-center gap-2 py-16 text-center">
              <AlertTriangle className="h-8 w-8 text-muted-foreground" />
              <p className="font-medium">Nenhuma matéria encontrada</p>
              <p className="text-sm text-muted-foreground">
                As matérias aparecem aqui após a varredura automática (a cada 1 hora).
              </p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Título</TableHead>
                  <TableHead>Fonte</TableHead>
                  <TableHead>Setor</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Capturada em</TableHead>
                  <TableHead className="text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((article) => (
                  <TableRow key={article.id}>
                    <TableCell className="max-w-[340px] truncate font-medium">
                      {article.title}
                    </TableCell>
                    <TableCell>{article.source ?? "—"}</TableCell>
                    <TableCell>
                      {article.sector ? (
                        <Badge variant="outline">{article.sector}</Badge>
                      ) : (
                        "—"
                      )}
                    </TableCell>
                    <TableCell>{statusBadge(article.status, article.status_label)}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {article.created_at}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          title="Abrir matéria original"
                          onClick={() =>
                            window.open(article.source_url, "_blank", "noopener,noreferrer")
                          }
                        >
                          <ExternalLink className="h-3.5 w-3.5" />
                        </Button>
                        <Button
                          size="sm"
                          onClick={() =>
                            router.visit(`/dashboard/scraped_articles/${article.id}`)
                          }
                        >
                          <Eye className="mr-1.5 h-3.5 w-3.5" />
                          Revisar
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

ScrapedArticlesIndex.layout = (page: ReactNode) => (
  <DashboardLayout>{page}</DashboardLayout>
);

export default ScrapedArticlesIndex;
